import React, { useRef, useState } from 'react';
import { Box, Button, Avatar, IconButton, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../utils/api';

export default function PhotoUpload({ label, value, onChange, size = 120, required = false, disabled = false, objectConfig = { fit: 'cover', position: 'center' } }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileObjectUrl, setFileObjectUrl] = useState(null);

  // When value (File object) changes, create an object URL for display
  React.useEffect(() => {
    if (value && typeof value === 'object' && value instanceof File) {
      // Create object URL from File object for display
      const url = URL.createObjectURL(value);
      setFileObjectUrl(url);
      setPreviewUrl(null); // Clear preview when File object is set
      return () => URL.revokeObjectURL(url);
    } else if (typeof value === 'string' && value.includes('/api/')) {
      // It's an API endpoint URL - fetch with token to get blob URL
      // Clear preview IMMEDIATELY when API URL is set (before async fetch)
      setPreviewUrl(null);
      
      const fetchAndSetPhoto = async () => {
        try {
          const res = await api.get(value);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          setFileObjectUrl(url);
          // Return cleanup function for blob URL
          return () => URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Failed to fetch photo from API:', err);
          setFileObjectUrl(null);
        }
      };
      fetchAndSetPhoto();
    } else {
      // value is null, undefined, or empty - clear everything
      if (fileObjectUrl) URL.revokeObjectURL(fileObjectUrl);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setFileObjectUrl(null);
      setPreviewUrl(null);
    }
  }, [value]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }

      // Compress image while keeping it as a File object
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 800x800 for photos)
          let width = img.width;
          let height = img.height;
          const maxSize = 800;
          
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress using canvas.toBlob (keeps it as binary File object)
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              // Create a File object from the blob
              const compressedFile = new File([blob], file.name || 'photo.jpg', { type: 'image/jpeg' });
              
              // Create a preview URL for display
              const previewObjectUrl = URL.createObjectURL(compressedFile);
              setPreviewUrl(previewObjectUrl);
              
              // Pass the File object to parent component
              if (onChange) onChange(compressedFile);
            },
            'image/jpeg',
            0.7 // Quality 0.7 for compression
          );
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClear = () => {
    if (onChange) onChange(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileObjectUrl) URL.revokeObjectURL(fileObjectUrl);
    setFileObjectUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Determine what to display - priority: previewUrl (new upload) > fileObjectUrl (File object) > value (string URL)
  const displayValue = previewUrl || fileObjectUrl || (typeof value === 'string' ? value : null);

  return (
    <Box sx={{ textAlign: 'center' }}>
      {label && (
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      )}
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Avatar
          src={displayValue}
          sx={{
            width: size,
            height: size,
            border: '3px solid',
            borderColor: displayValue ? 'success.main' : required ? 'error.light' : 'grey.300',
            backgroundColor: 'grey.100',
            '& img': {
              objectFit: objectConfig?.fit || 'cover',
              objectPosition: objectConfig?.position || 'center'
            }
          }}
        >
          {!displayValue && <PhotoCameraIcon sx={{ fontSize: size / 2, color: 'grey.400' }} />}
        </Avatar>
        {displayValue && (
          <IconButton
            size="small"
            onClick={handleClear}
            disabled={disabled}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              backgroundColor: disabled ? 'grey.300' : 'error.main',
              color: disabled ? 'grey.500' : 'white',
              '&:hover': { backgroundColor: disabled ? 'grey.300' : 'error.dark' },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
        <input
          type="file"
          ref={cameraInputRef}
          accept="image/*"
          capture="user"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          startIcon={<PhotoCameraIcon />}
          onClick={() => cameraInputRef.current?.click()}
        >
          Camera
        </Button>
        
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
        {value ? 'Photo uploaded' : 'Max 5MB'}
      </Typography>
    </Box>
  );
}
